import React from 'react';

import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { isRowApproved, isRowManuallyApproved } from '../../manualOverridePersistence';
import { formatPrice } from '../../shared/checklistUtils';
import type { ManualOverrideState } from '../../types';

import DealFieldBox from './DealFieldBox';
import DealFieldLabel from './DealFieldLabel';
import InclusionRow from './InclusionRow';
import type { DealOptionsInclusionsDealRow } from './types';

interface Props {
  deal: DealOptionsInclusionsDealRow;
  manualOverrides: ManualOverrideState;
  showAutoApproved: boolean;
  onApprovalChange: (rowId: string) => void;
  onUnapprovalChange: (rowId: string) => void;
}

export default function DealCard({
  deal,
  manualOverrides,
  showAutoApproved,
  onApprovalChange,
  onUnapprovalChange,
}: Props) {
  const visibleInclusions = deal.inclusions.filter((inc) => {
    if (showAutoApproved) {
      return true;
    }
    return !isRowApproved(inc, manualOverrides);
  });

  return (
    <Paper variant="outlined" sx={{ borderRadius: 1.25, overflow: 'hidden' }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr', columnGap: 3, rowGap: 0.5 }}>
          <DealFieldLabel>DEAL NAME</DealFieldLabel>
          <DealFieldLabel>ROOM NAME</DealFieldLabel>
          <DealFieldLabel>SELL PRICE</DealFieldLabel>
          <DealFieldLabel>COST PRICE</DealFieldLabel>
          <DealFieldLabel>% MARGIN</DealFieldLabel>
          <DealFieldLabel>RACK RATE</DealFieldLabel>

          <DealFieldBox value={deal.dealOptionName || '—'} />
          <DealFieldBox value={deal.roomName || '—'} />
          <DealFieldBox value={formatPrice(deal.sellPrice, deal.currency)} />
          <DealFieldBox value={formatPrice(deal.costPrice, deal.currency)} />
          <DealFieldBox value={deal.marginPercent != null ? `${deal.marginPercent}%` : '—'} />
          <DealFieldBox value={formatPrice(deal.rackRate, deal.currency)} />
        </Box>
      </Box>

      {visibleInclusions.length === 0 ? (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {deal.inclusions.length === 0 ? 'No inclusions configured.' : 'All inclusions approved.'}
          </Typography>
        </Box>
      ) : (
        <TableContainer>
          <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 1560 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 200 }}>Inclusion Name</TableCell>
                <TableCell sx={{ width: 540 }}>Inclusion Description</TableCell>
                <TableCell sx={{ width: 100 }}>Type</TableCell>
                <TableCell sx={{ width: 170 }}>Deal Option Inclusion RRP</TableCell>
                <TableCell sx={{ width: 150 }}>Inclusion Asset Value</TableCell>
                <TableCell sx={{ width: 130 }}>Total Value</TableCell>
                <TableCell sx={{ width: 140 }}>RRP Comparison</TableCell>
                <TableCell sx={{ width: 130 }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleInclusions.map((inc) => (
                <InclusionRow
                  key={inc.id}
                  currency={deal.currency}
                  inc={inc}
                  approved={isRowApproved(inc, manualOverrides)}
                  manuallyApproved={isRowManuallyApproved(inc, manualOverrides)}
                  onApprovalChange={onApprovalChange}
                  onUnapprovalChange={onUnapprovalChange}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}
